import React, { Component } from "react";

import { Container, Button } from "react-bootstrap";
import { Stack, Typography, Box } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

class RichTextViewer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 0,
    };
  }

  get steps() {
    return this.props.game.rounds?.length ?? 1;
  }

  getContent() {
    // Get content from either direct props or config object
    const { content, config } = this.props;
    return content || (config && config.content) || null;
  }

  get currentContent() {
    const content = this.getContent();
    return Array.isArray(content) ? content[this.state.step] : content;
  }

  get title() {
    // Get title from either direct props or config object
    const { title, config } = this.props;
    return title || (config && config.title) || null;
  }

  canStepBack = () => {
    return this.state.step > 0;
  };

  canStepNext = () => {
    return this.state.step < this.steps - 1;
  };

  // go back to previous step
  prevStep = (e) => {
    e.preventDefault();
    const { step } = this.state;
    this.setState({ step: step - 1 });
  };

  // proceed to the next step
  nextStep = (e) => {
    e.preventDefault(); // Add this to prevent default form submission
    this.props.onSubmit?.();
  };

  render() {
    const content = this.currentContent;
    const title = this.title;
    const isLastStep = this.state.step === this.steps - 1;

    return (
      <Container>
        {title && (
          <Typography variant="h4" component="h1" gutterBottom align="center">
            {title}
          </Typography>
        )}

        <Box
          sx={{
            minHeight: '400px',
            padding: 3,
            backgroundColor: '#fafafa',
            borderRadius: 2,
            border: '1px solid #e0e0e0',
            marginBottom: 3
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              img: ({ node, ...props }) => (
                <img
                  {...props}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    margin: '16px 0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                  alt={props.alt || 'Image'}
                />
              ),
              h1: ({ node, ...props }) => (
                <Typography variant="h4" component="h1" gutterBottom {...props} />
              ),
              h2: ({ node, ...props }) => (
                <Typography variant="h5" component="h2" gutterBottom {...props} />
              ),
              h3: ({ node, ...props }) => (
                <Typography variant="h6" component="h3" gutterBottom {...props} />
              ),
              p: ({ node, ...props }) => (
                <Typography variant="body1" paragraph {...props} />
              ),
              ul: ({ node, ...props }) => (
                <Box component="ul" sx={{ pl: 3, mb: 2 }} {...props} />
              ),
              ol: ({ node, ...props }) => (
                <Box component="ol" sx={{ pl: 3, mb: 2 }} {...props} />
              ),
              a: ({ node, ...props }) => (
                <Typography
                  component="a"
                  color="primary"
                  sx={{ textDecoration: 'underline' }}
                  {...props}
                />
              ),
              code: ({ node, inline, ...props }) => {
                if (inline) {
                  return (
                    <Box
                      component="code"
                      sx={{
                        backgroundColor: '#f5f5f5',
                        padding: '2px 4px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.9em'
                      }}
                      {...props}
                    />
                  );
                }
                return (
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: '#f5f5f5',
                      padding: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      margin: '16px 0'
                    }}
                  >
                    <Box
                      component="code"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.9em'
                      }}
                      {...props}
                    />
                  </Box>
                );
              },
              blockquote: ({ node, ...props }) => (
                <Box
                  component="blockquote"
                  sx={{
                    borderLeft: '4px solid #1976d2',
                    paddingLeft: 2,
                    margin: '16px 0',
                    fontStyle: 'italic',
                    backgroundColor: '#f8f9fa',
                    padding: 2,
                    borderRadius: '0 4px 4px 0'
                  }}
                  {...props}
                />
              ),
              table: ({ node, ...props }) => (
                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    margin: '16px 0',
                    '& th, & td': {
                      border: '1px solid #ddd',
                      padding: '8px 12px',
                      textAlign: 'left'
                    },
                    '& th': {
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold'
                    }
                  }}
                  {...props}
                />
              )
            }}
          >
            {content || "No content to display"}
          </ReactMarkdown>
        </Box>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="end"
          spacing={2}
        >
          <Button
            variant="primary"
            onClick={this.nextStep}
            disabled={false} // Remove the disabled state for the Finish button
            fullWidth
            style={{ width: '100%' }}
          >
            {isLastStep ? 'Finish' : 'Next'}
          </Button>
        </Stack>
      </Container>
    );
  }
}

export default RichTextViewer;